import React, { useEffect, useRef } from 'react';

const EmailPreviewFrame = ({ htmlContent, cssContent, previewMode, styleSettings }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current) return;

    const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    
    // Additional CSS to fix common styling issues in preview
    const additionalCSS = `
      /* Fix Bootstrap-dependent components */
      .d-flex { display: flex !important; }
      .flex-column { flex-direction: column !important; }
      .flex-row { flex-direction: row !important; }
      .justify-content-center { justify-content: center !important; }
      .justify-content-between { justify-content: space-between !important; }
      .justify-content-around { justify-content: space-around !important; }
      .align-items-center { align-items: center !important; }
      .text-center { text-align: center !important; }
      .text-start { text-align: left !important; }
      .text-end { text-align: right !important; }
      .position-relative { position: relative !important; }
      .position-absolute { position: absolute !important; }
      .top-0 { top: 0 !important; }
      .start-0 { left: 0 !important; }
      .w-100 { width: 100% !important; }
      .h-100 { height: 100% !important; }
      .img-fluid { max-width: 100%; height: auto; }
      .object-fit-cover { object-fit: cover !important; }
      .rounded { border-radius: 0.25rem !important; }
      .mb-2 { margin-bottom: 0.5rem !important; }
      .mb-3 { margin-bottom: 1rem !important; }
      .mb-4 { margin-bottom: 1.5rem !important; }
      .mb-5 { margin-bottom: 3rem !important; }
      .mt-2 { margin-top: 0.5rem !important; }
      .mt-3 { margin-top: 1rem !important; }
      .mt-4 { margin-top: 1.5rem !important; }
      .px-2 { padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
      .px-3 { padding-left: 1rem !important; padding-right: 1rem !important; }
      .py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
      .py-3 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
      .p-2 { padding: 0.5rem !important; }
      .p-3 { padding: 1rem !important; }
      .me-2 { margin-right: 0.5rem !important; }
      .gap-2 { gap: 0.5rem !important; }
      .gap-3 { gap: 1rem !important; }
      
      /* Fix for banner images and other containers */
      .banner-container img {
        max-width: 100%;
        height: auto;
        display: block;
      }
      
      /* Hide edit buttons */
      .section-header {
        display: none !important;
      }
      
      /* Add good default spacing between components */
      .email-preview-component {
        margin-bottom: 20px;
      }
      
      /* Fix font rendering */
      body {
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
      
      /* Ensure images display properly */
      img {
        max-width: 100%;
        height: auto;
        display: block;
      }
      
      /* Fix image errors */
      img.error {
        border: 1px solid #dc3545;
        background-color: #f8d7da;
        min-height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #721c24;
        font-size: 14px;
      }
      
      /* Fix footer component specific styling */
      .email-footer {
        margin-top: 20px;
        padding: 20px 0;
        text-align: center;
      }
      
      /* Fix social links in footer */
      .social-links {
        display: flex;
        justify-content: center;
        gap: 10px;
        margin: 10px 0;
      }
      
      /* Fix header component styling */
      .email-header {
        padding: 10px 0;
      }
      
      /* Ensure nav links display properly */
      ul.nav {
        display: flex;
        list-style: none;
        padding: 0;
        margin: 0;
      }
      
      .nav-item {
        margin-right: 10px;
      }
      
      .nav-link {
        text-decoration: none;
        color: inherit;
      }
    `;
    
    // Create a complete HTML document with CSS and body content
    const completeHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Email Preview</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${cssContent}
            ${additionalCSS}
            ${previewMode === "mobile" ? `
            .email-container {
                max-width: 320px !important;
                padding: 10px !important;
            }
            ` : ''}
          </style>
        </head>
        <body>
          ${htmlContent}
          <script>
            // Handle image loading errors safely
            document.addEventListener('DOMContentLoaded', () => {
              try {
                const images = document.querySelectorAll('img');
                images.forEach(img => {
                  img.onerror = function() {
                    console.error('Image failed to load:', this.src);
                    this.onerror = null;
                    this.classList.add('error');
                    
                    // Create fallback based on component context
                    let fallbackText = 'Image Error';
                    const parentClass = this.parentElement.className || '';
                    
                    if (parentClass.includes('header-logo')) {
                      fallbackText = 'Logo Image';
                      this.style.maxHeight = '60px';
                      this.style.minWidth = '150px';
                    } else if (parentClass.includes('banner')) {
                      fallbackText = 'Banner Image';
                      this.style.minHeight = '200px';
                    }
                    
                    this.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y4ZDdkYSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNzIxYzI0Ij5JbWFnZSBFcnJvcjwvdGV4dD48L3N2Zz4=';
                  };
                  
                  // Clean up the URLs with timestamps
                  const src = img.getAttribute('src');
                  if (src && src.includes('&t=')) {
                    img.setAttribute('src', src.split('&t=')[0]);
                  }
                });
              } catch (e) {
                console.error('Error handling images:', e);
              }
            });
            
            // Add class to components for better styling
            document.addEventListener('DOMContentLoaded', () => {
              try {
                // Fix TitleDescription components
                const titleSections = document.querySelectorAll('.preview-section');
                titleSections.forEach(section => {
                  if (section.innerHTML.includes('TitleDescription')) {
                    section.classList.add('title-description-component');
                  }
                  if (section.innerHTML.includes('Footer') && !section.classList.contains('email-footer')) {
                    section.classList.add('email-footer-container');
                  }
                });
              } catch (e) {
                console.error('Error processing sections:', e);
              }
            });
          </script>
        </body>
      </html>
    `;
    
    // Set the iframe content
    iframeDocument.open();
    iframeDocument.write(completeHtml);
    iframeDocument.close();
  }, [htmlContent, cssContent, styleSettings, previewMode]);

  const frameStyle = {
    width: previewMode === "mobile" ? "375px" : "100%",
    height: "100%",
    border: 'none',
    borderRadius: '8px',
    backgroundColor: 'white',
    margin: previewMode === "mobile" ? "0 auto" : "0",
    display: "block"
  };

  return (
    <iframe 
      ref={iframeRef}
      title="Email Preview"
      style={frameStyle}
      frameBorder="0"
    />
  );
};

export default EmailPreviewFrame;